// bluaIQ — TASK-WEB-16 (FASE 2 paso 3): the REAL "Cuantificar planos" flow for
// the project in the route (NOT the static demo, which is preserved as-is).
//
// upload PDF → POST source-document (name + short hash shown) → button fires
// POST /quantify → poll to a TERMINAL state, always:
//   done    → parent fetches the persisted payload, gates it through
//             validatePayloadV3 and renders it via the SAME path as the demo;
//   failed  → honest card (worker copy ES/EN verbatim, "Nada fue inventado."
//             visible; worker_not_configured gets its friendly copy);
//   partial → "Lectura parcial disponible" banner + verbatim copy;
//   poll timeout → clear retryable state. NEVER a spinner without an exit.
//
// Owner gating is enforced at the route (App.tsx) AND server-side on every
// endpoint this component calls. This flow makes zero purchase/downstream
// writes — it uploads a plan and reads back a candidate/read-only payload.

import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import {
  SESSION_EXPIRED,
  deriveQuantifyCard,
  deriveRejectionCard,
  getProjectPayload,
  hydratePersistedState,
  pollJobUntilTerminal,
  shortHash,
  startQuantify,
  uploadSourceDocument,
} from './takeoffQuantifyClient'
import type {
  PersistedRejection,
  QuantifyCard,
  SourceDocumentRef,
  UploadedSourceDocument,
} from './takeoffQuantifyClient'

const ACCENT = '#00BCD4'

const FLOW_COPY = {
  uploadPrompt: { en: 'Upload your plan (PDF)', es: 'Suba su plano (PDF)' },
  uploadHint: {
    en: 'PDF only, up to 50MB. The file is stored by its content fingerprint (hash) — replacing it later creates a new revision.',
    es: 'Solo PDF, hasta 50MB. El archivo se guarda por su huella de contenido (hash) — reemplazarlo después crea una nueva revisión.',
  },
  uploading: { en: 'Uploading…', es: 'Subiendo…' },
  uploadedReused: {
    en: 'This exact file was already uploaded — reusing it.',
    es: 'Este archivo exacto ya estaba subido — se reutiliza.',
  },
  fileLabel: { en: 'Plan', es: 'Plano' },
  hashLabel: { en: 'fingerprint', es: 'huella' },
  quantifyButton: { en: 'Quantify plans', es: 'Cuantificar planos' },
  // Honest in-progress copy (Codex requirement — verbatim from the task spec).
  reading: {
    en: 'Reading your plans — verified measurements, not assumptions',
    es: 'Leyendo sus planos — mediciones verificadas, no suposiciones',
  },
  jobState: { en: 'Status', es: 'Estado' },
  retry: { en: 'Check again', es: 'Consultar de nuevo' },
  tryAgain: { en: 'Try again', es: 'Intentar de nuevo' },
  uploadError: {
    en: 'The file could not be uploaded. Nothing was quantified.',
    es: 'El archivo no se pudo subir. No se cuantificó nada.',
  },
  // TASK-WEB-22 (Codex): friendly copy for an expired session — never a
  // technical code. The plan is never lost by a session expiry.
  sessionExpired: {
    en: 'Your session expired — please sign in again; your plan was not lost.',
    es: 'Su sesión expiró — vuelva a iniciar sesión; su plano no se perdió.',
  },
  // TASK-WEB-24: shown while checking for a previously persisted result.
  hydrating: {
    en: 'Checking for your previous quantification…',
    es: 'Buscando su cuantificación anterior…',
  },
  // TASK-WEB-33 (Codex NEEDS FIX): the persisted-rejection state. The last
  // attempt was a sealed refusal — hydration must show IT, never silently
  // resurrect an older document's successful payload.
  rejectedIntro: {
    en: 'The last plan you submitted could not be quantified:',
    es: 'El último plano que envió no pudo cuantificarse:',
  },
  showPreviousResult: {
    en: 'View previous result',
    es: 'Ver resultado anterior',
  },
} as const

type FlowPhase =
  | { name: 'hydrating' }
  | { name: 'idle' }
  | { name: 'uploading' }
  | { name: 'uploaded'; doc: UploadedSourceDocument; reused: boolean }
  | { name: 'quantifying'; doc: UploadedSourceDocument; jobStatus: string }
  | { name: 'card'; doc: UploadedSourceDocument; card: QuantifyCard }
  | { name: 'upload_error'; detail: string }
  | {
      name: 'rejected'
      rejection: PersistedRejection
      card: QuantifyCard
      previousPayload: unknown | null
      previousDocument: SourceDocumentRef | null
    }

export default function TakeoffRealQuantifyFlow({
  projectId,
  isEs,
  onValidatedPayload,
  skipHydration = false,
}: {
  projectId: string
  isEs: boolean
  /** Called with the freshly fetched payload on `done` (or on an EXPLICIT
   *  user selection of a previous result). The PARENT runs the
   *  validatePayloadV3 gate and renders through the same path as the demo.
   *  TASK-WEB-33: `document` carries the payload's source-document identity
   *  (name + fingerprint) — the parent must show it with the rendered result
   *  so a previous payload is never mistaken for the latest attempt.
   *  TASK-WEB-36: `payloadSha256` carries the server-computed canonical-JSON
   *  hash of the persisted payload (the determinism surface for the auditor
   *  view). Undefined when the payload came from a source without a persisted
   *  hash (e.g. an explicit "previous result" selection off a rejection). */
  onValidatedPayload: (
    payload: unknown,
    document?: SourceDocumentRef | null,
    payloadSha256?: string | null,
  ) => void
  /** TASK-WEB-25 (ceremony blocker #4): when this mount is a user-requested
   *  reset ("Upload new plan"), hydration must be SKIPPED — otherwise the
   *  mount instantly re-hydrates the persisted payload and the reset appears
   *  dead (reset↔rehydrate loop). In-memory only: a reload still re-hydrates,
   *  so an accidental reset never loses the persisted result. */
  skipHydration?: boolean
}) {
  const L = (k: { en: string; es: string }) => (isEs ? k.es : k.en)
  const [phase, setPhase] = useState<FlowPhase>(skipHydration ? { name: 'idle' } : { name: 'hydrating' })
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  // TASK-WEB-22 (Codex ceremony blocker): production requireAuth demands a
  // Bearer token — every call carries a fresh Clerk token (the poll refetches
  // one per tick, since a run can outlast a single short-lived token).
  const { getToken } = useAuth()
  // STALE-ID RULE: the only document id this flow ever sends to /quantify is
  // the one returned by the upload in THIS UI session (held in `phase`).

  // TASK-WEB-24 (Codex ceremony blocker #3): re-hydrate the last persisted
  // state on mount so the result SURVIVES language switches, reloads and
  // fresh visits — the owner must never lose a quantification to navigation.
  // TASK-WEB-33 (Codex NEEDS FIX): when the LAST terminal attempt was a
  // sealed rejection, hydration shows the REJECTION (with the rejected
  // document's identity) — it never silently resurrects an older document's
  // successful payload; that stays available only through an explicit user
  // selection. Nothing persisted yet (or transient failure) ⇒ upload state.
  useEffect(() => {
    if (skipHydration) return undefined // user-requested reset: straight to upload
    let cancelled = false
    void (async () => {
      try {
        const state = await hydratePersistedState(projectId, getToken)
        if (cancelled) return
        if (state.kind === 'rejection') {
          const card = deriveRejectionCard(state.rejection)
          if (card) {
            setPhase({
              name: 'rejected', rejection: state.rejection, card,
              previousPayload: state.previousPayload, previousDocument: state.previousDocument,
            })
            return
          }
          // Defensive: a rejection that derives no card falls through honestly.
        }
        if (state.kind === 'payload' && state.payload) {
          onValidatedPayload(state.payload, state.document, state.payloadSha256)
          return // the parent renders the payload; this flow unmounts
        }
        setPhase({ name: 'idle' })
      } catch {
        // session_expired during hydration: the route gate governs access —
        // fall back to the upload state, nothing is lost server-side.
        if (!cancelled) setPhase({ name: 'idle' })
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId])

  async function handleFile(file: File | undefined) {
    if (!file) return
    setPhase({ name: 'uploading' })
    try {
      const token = await getToken()
      const { sourceDocument, reused } = await uploadSourceDocument(projectId, file, token)
      setPhase({ name: 'uploaded', doc: sourceDocument, reused })
    } catch (err) {
      setPhase({ name: 'upload_error', detail: err instanceof Error ? err.message : String(err) })
    }
  }

  async function handleQuantify(doc: UploadedSourceDocument) {
    setPhase({ name: 'quantifying', doc, jobStatus: 'queued' })
    try {
      const { jobId } = await startQuantify(projectId, doc.id, await getToken())
      const outcome = await pollJobUntilTerminal(projectId, jobId, {
        getToken,
        onTick: (job) => setPhase({ name: 'quantifying', doc, jobStatus: job.status }),
      })
      const card = deriveQuantifyCard(outcome)
      if (card) {
        setPhase({ name: 'card', doc, card })
        return
      }
      // done → fetch the persisted payload; the parent gates it through
      // validatePayloadV3 before anything renders (same flow as the demo).
      // TASK-WEB-33: the document identity travels with the payload.
      const { payload, document, payloadSha256 } = await getProjectPayload(projectId, await getToken())
      onValidatedPayload(payload, document, payloadSha256)
      setPhase({ name: 'uploaded', doc, reused: true })
    } catch (err) {
      setPhase({
        name: 'card',
        doc,
        card: deriveQuantifyCard({
          kind: 'terminal',
          job: {
            id: '', status: 'failed', workerStatus: null, workerCopy: null,
            websiteError: err instanceof Error ? err.message : String(err),
            runId: '', payloadId: null,
          },
        })!,
      })
    }
  }

  const docLine = (doc: UploadedSourceDocument) => (
    <p
      data-testid="takeoff-quantify-uploaded-doc"
      className="text-[11px] font-[800] text-[#1C1A17] break-words min-w-0"
      style={{ overflowWrap: 'anywhere' }}
    >
      {L(FLOW_COPY.fileLabel)}: {doc.originalFilename ?? '—'}{' '}
      <span className="font-[700] text-[#6B655C]">
        ({L(FLOW_COPY.hashLabel)}: {shortHash(doc.sourceDocumentHash)})
      </span>
    </p>
  )

  return (
    <div
      data-testid="takeoff-real-quantify-flow"
      className="rounded-xl border border-[#E4EDF6] bg-white p-6 min-w-0 flex flex-col items-center gap-3 text-center"
    >
      {phase.name === 'hydrating' && (
        <p data-testid="takeoff-quantify-hydrating" className="text-[12px] font-[800] text-[#6B655C]">
          {L(FLOW_COPY.hydrating)}
        </p>
      )}

      {(phase.name === 'idle' || phase.name === 'upload_error') && (
        <>
          <p className="text-[12px] font-[800] text-[#6B655C]">{L(FLOW_COPY.uploadPrompt)}</p>
          {phase.name === 'upload_error' && (
            <div
              data-testid="takeoff-quantify-upload-error"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[11px] font-[800] text-red-800 break-words min-w-0"
              style={{ overflowWrap: 'anywhere' }}
            >
              {/* Session expiry gets the friendly copy — never a technical code. */}
              {phase.detail === SESSION_EXPIRED
                ? L(FLOW_COPY.sessionExpired)
                : <>{L(FLOW_COPY.uploadError)} ({phase.detail})</>}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            data-testid="takeoff-quantify-file-input"
            className="hidden"
            onChange={(e) => void handleFile(e.target.files?.[0])}
          />
          <button
            type="button"
            data-testid="takeoff-quantify-upload-button"
            onClick={() => fileInputRef.current?.click()}
            className="rounded-lg px-4 py-2 text-[12px] font-[900] text-white"
            style={{ backgroundColor: ACCENT }}
          >
            {L(FLOW_COPY.uploadPrompt)}
          </button>
          <p className="text-[9px] font-[700] text-[#9E9890] max-w-md break-words min-w-0" style={{ overflowWrap: 'anywhere' }}>
            {L(FLOW_COPY.uploadHint)}
          </p>
        </>
      )}

      {phase.name === 'uploading' && (
        <p data-testid="takeoff-quantify-uploading" className="text-[12px] font-[800] text-[#6B655C]">
          {L(FLOW_COPY.uploading)}
        </p>
      )}

      {phase.name === 'uploaded' && (
        <>
          {docLine(phase.doc)}
          {phase.reused && (
            <p className="text-[10px] font-[700] text-[#6B655C]">{L(FLOW_COPY.uploadedReused)}</p>
          )}
          <button
            type="button"
            data-testid="takeoff-real-quantify-button"
            onClick={() => void handleQuantify(phase.doc)}
            className="rounded-lg px-4 py-2 text-[12px] font-[900] text-white"
            style={{ backgroundColor: ACCENT }}
          >
            {L(FLOW_COPY.quantifyButton)}
          </button>
        </>
      )}

      {phase.name === 'quantifying' && (
        <>
          {docLine(phase.doc)}
          <div
            data-testid="takeoff-quantify-progress"
            data-job-status={phase.jobStatus}
            className="flex flex-col items-center gap-2"
          >
            <div
              className="h-6 w-6 rounded-full border-2 border-t-transparent animate-spin"
              style={{ borderColor: ACCENT, borderTopColor: 'transparent' }}
            />
            <p className="text-[11px] font-[800] text-[#0B7285] break-words min-w-0" style={{ overflowWrap: 'anywhere' }}>
              {L(FLOW_COPY.reading)}
            </p>
            <p className="text-[9px] font-[700] text-[#9E9890]">
              {L(FLOW_COPY.jobState)}: {phase.jobStatus}
            </p>
          </div>
        </>
      )}

      {/* TASK-WEB-33: persisted rejection — the LAST attempt was a sealed
          refusal. Shows the rejected document's identity + the verbatim
          bilingual copy, and stays until the user explicitly uploads a new
          plan or explicitly selects the previous result (never automatic).
          Survives remounts/reloads (server-persisted) and language switches
          (phase state + bilingual copy). */}
      {phase.name === 'rejected' && (
        <>
          <p className="text-[11px] font-[800] text-[#6B655C]">{L(FLOW_COPY.rejectedIntro)}</p>
          <p
            data-testid="takeoff-rejected-doc"
            className="text-[11px] font-[800] text-[#1C1A17] break-words min-w-0"
            style={{ overflowWrap: 'anywhere' }}
          >
            {L(FLOW_COPY.fileLabel)}: {phase.rejection.document?.originalFilename ?? '—'}{' '}
            <span className="font-[700] text-[#6B655C]">
              ({L(FLOW_COPY.hashLabel)}: {shortHash(phase.rejection.document?.sourceDocumentHash ?? null)})
            </span>
          </p>
          <div
            data-testid="takeoff-rejected-card"
            data-card-code={phase.card.code}
            className={`rounded-lg border px-3 py-3 min-w-0 max-w-lg space-y-1 text-left ${
              phase.card.kind === 'partial' ? 'border-amber-200 bg-amber-50' : 'border-[#E4EDF6] bg-[#FAFBFD]'
            }`}
          >
            <p className="text-[12px] font-[900] text-[#1C1A17] break-words min-w-0" style={{ overflowWrap: 'anywhere' }}>
              {L(phase.card.title)}
            </p>
            <p
              data-testid="takeoff-rejected-card-body"
              className="text-[11px] font-[700] text-[#6B655C] break-words min-w-0"
              style={{ overflowWrap: 'anywhere' }}
              title={isEs ? phase.card.body.en : phase.card.body.es}
            >
              {L(phase.card.body)}
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              data-testid="takeoff-rejected-upload-new-button"
              onClick={() => setPhase({ name: 'idle' })}
              className="rounded-lg px-4 py-2 text-[12px] font-[900] text-white"
              style={{ backgroundColor: ACCENT }}
            >
              {L(FLOW_COPY.uploadPrompt)}
            </button>
            {phase.previousPayload != null && (
              <button
                type="button"
                data-testid="takeoff-rejected-show-previous-button"
                onClick={() => onValidatedPayload(phase.previousPayload, phase.previousDocument)}
                className="rounded-lg px-4 py-2 text-[12px] font-[900] border break-words min-w-0"
                style={{ borderColor: ACCENT, color: '#1C1A17', overflowWrap: 'anywhere' }}
              >
                {L(FLOW_COPY.showPreviousResult)}
                {phase.previousDocument?.originalFilename
                  ? ` — ${phase.previousDocument.originalFilename} (${L(FLOW_COPY.hashLabel)}: ${shortHash(phase.previousDocument.sourceDocumentHash ?? null)})`
                  : ''}
              </button>
            )}
          </div>
        </>
      )}

      {phase.name === 'card' && (
        <>
          {docLine(phase.doc)}
          <div
            data-testid="takeoff-quantify-terminal-card"
            data-card-kind={phase.card.kind}
            data-card-code={phase.card.code}
            className={`rounded-lg border px-3 py-3 min-w-0 max-w-lg space-y-1 text-left ${
              phase.card.kind === 'partial'
                ? 'border-amber-200 bg-amber-50'
                : 'border-[#E4EDF6] bg-[#FAFBFD]'
            }`}
          >
            <p className="text-[12px] font-[900] text-[#1C1A17] break-words min-w-0" style={{ overflowWrap: 'anywhere' }}>
              {L(phase.card.title)}
            </p>
            {/* Worker copy renders VERBATIM in the active language — the other
                language stays available as title-attribute provenance. */}
            <p
              data-testid="takeoff-quantify-card-body"
              className="text-[11px] font-[700] text-[#6B655C] break-words min-w-0"
              style={{ overflowWrap: 'anywhere' }}
              title={isEs ? phase.card.body.en : phase.card.body.es}
            >
              {L(phase.card.body)}
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {phase.card.retryable && (
              <button
                type="button"
                data-testid="takeoff-quantify-retry-button"
                onClick={() => void handleQuantify(phase.doc)}
                className="rounded-lg px-4 py-2 text-[12px] font-[900] text-white"
                style={{ backgroundColor: ACCENT }}
              >
                {phase.card.kind === 'poll_timeout' ? L(FLOW_COPY.retry) : L(FLOW_COPY.tryAgain)}
              </button>
            )}
            <button
              type="button"
              data-testid="takeoff-quantify-new-upload-button"
              onClick={() => setPhase({ name: 'idle' })}
              className="rounded-lg px-4 py-2 text-[12px] font-[900] border"
              style={{ borderColor: ACCENT, color: '#1C1A17' }}
            >
              {L(FLOW_COPY.uploadPrompt)}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
